import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryStringCaseInsensitive from "@insite/client-framework/Common/Utilities/parseQueryStringCaseInsensitive";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getProductsYMMD from "@store/Store/Components/RsswYMMD/Handlers/getProductsYMMD";
import { useProductListYmmdPage } from "@store/Store/Components/RsswYMMD/Hooks/useYMMD";
import { ymmdAvailableDimensionsSelector } from "@store/Store/Components/RsswYMMD/RsswYMMDSelector";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import { GetYMMDApiParameter } from "@store/Types/ApiModels";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasHistory;

export interface ProductListFiltersYmmdContainerStyles {
    wrapper?: InjectableCss;
    changeDiameterButton?: ButtonPresentationProps;
    diameterLabel?: TypographyPresentationProps;
    wrapperDiameters?: InjectableCss;
}

export const productListFiltersYmmdContainerStyles: ProductListFiltersYmmdContainerStyles = {
    changeDiameterButton: {
        variant: "secondary",
        css: css`
            min-height: 35px;
            margin-right: 1em;
            margin-bottom: 1em;

            ${TypographyStyle} {
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.base};
            }
        `,
    },
    diameterLabel: {
        size: TYPOGRAPHY.size.base,
        color: COLORS.secondary.base,
        css: css`
            display: block;
            margin-bottom: 10px;
        `,
    },
};

const ProductListFiltersYmmd = ({ id, history }: Props) => {
    const dispatch = useDispatch();
    const location = useSelector(getLocation);
    const productListYmmdPage = useProductListYmmdPage();
    const availableDimensions = useSelector(ymmdAvailableDimensionsSelector);
    const [currentParams, setCurrentParams] = useState<GetYMMDApiParameter>({});

    const getParams = () => {
        if (location && location.search) {
            const parsedYmmdQuery = parseQueryStringCaseInsensitive(location.search, {
                year: "",
                make: "",
                model: "",
                diameter: "",
                key: "diameter",
            });
            setCurrentParams(parsedYmmdQuery);
        }
    };

    useEffect(() => {
        getParams();
    }, [location]);

    const changeDiameter = (diameter: string) => {
        getParams();
        const newParams = { ...currentParams, diameter };
        setCurrentParams(newParams);
        const ymmdPageUrl = `${productListYmmdPage}?year=${currentParams.year}&make=${currentParams.make}&model=${currentParams.model}&diameter=${diameter}`;

        dispatch(getProductsYMMD(newParams));
        if (history && productListYmmdPage) {
            history.push(ymmdPageUrl);
        }
    };

    if (!availableDimensions || !currentParams.diameter) {
        return null;
    }

    const styles = productListFiltersYmmdContainerStyles;

    const renderDiameterLabel = (diameter: string, currentDiameter: string) => {
        const diff = parseInt(diameter, 10) - parseInt(currentDiameter, 10);
        if (diff === 0) {
            return `${diameter} (=)`;
        }
        if (diff > 0) {
            return `${diameter} (+${diff})`;
        }

        return `${diameter} (${diff})`;
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.wrapperDiameters}>
                <Typography {...styles.diameterLabel}>{translate("Compatible Diameters")} : </Typography>
                {availableDimensions.map(diameter => {
                    const diff = parseInt(diameter, 10) - parseInt(currentParams.diameter as string, 10);

                    return (
                        <Button
                            key={diameter}
                            {...styles.changeDiameterButton}
                            variant={diff === 0 ? `primary` : `secondary`}
                            onClick={() => changeDiameter(diameter)}
                        >
                            {renderDiameterLabel(diameter, currentParams.diameter as string)}
                        </Button>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const productListFiltersYmmd: WidgetModule = {
    component: withHistory(ProductListFiltersYmmd),
    definition: {
        group: "Common",
        displayName: "Product List Filters Ymmd",
        isSystem: true,
    },
};

export default productListFiltersYmmd;
